import React, { useContext } from 'react';
import styled from 'styled-components';
import { Button, Card, Icon, Link, Space, Text } from '@bb/elements-web';
import { LayoutContext } from 'contexts';
import AnsweringServiceContext, {
  AnsweringServiceSubscribeStep,
} from '../AnsweringServiceContext';
import ListingTable from './ListingTable';
import ManageListingsContext from './ManageListingsContext';
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import PhoneBoxPopulated from './PhoneBoxPopulated';

export const TOGGLE_CARD_HEIGHT = 217; // Also used in the Listing Table

/**
 * The main Manage Listings page, the second step in the flow for a new user
 * signing up for an Answering Service.
 */
const ManageListingsLayout: React.FC = () => {
  const { getSiderCurrentWidth } = useContext(LayoutContext);
  const {
    hasActiveAnsweringService,
    setCurrentStep,
    billingRedirection,
    viewingLevel,
  } = useContext(AnsweringServiceContext);
  const { containerRef, footerRef } = useContext(ManageListingsContext);

  return (
    <>
      <StyledHeader>
        <BackArrow onClick={billingRedirection}>
          <Icon icon="fullArrowLeftOutlined" size={20} color="primary" />
        </BackArrow>
        <h1>
          {viewingLevel === 'org'
            ? 'Your Brokerage Subscription'
            : 'Your Subscription'}
        </h1>
      </StyledHeader>
      <StyledManageListingsContainer
        ref={containerRef}
        $hasCards={hasActiveAnsweringService}
      >
        {hasActiveAnsweringService && (
          <>
            <CurrentSubscriptionCard />
            <Card dataCy="phone-card">
              <Space direction="vertical" size="middle">
                <PhoneBoxPopulated />
                <Text type="secondary" dataCy="listing-default-text">
                  Add this number to your MLS listings to accept showing
                  requests by phone.
                </Text>
              </Space>
            </Card>
          </>
        )}
        <StyledListingsTableContainer dataCy="listing-table-container">
          <ListingTable />
        </StyledListingsTableContainer>
      </StyledManageListingsContainer>
      {!hasActiveAnsweringService && (
        <StyledFooter $left={getSiderCurrentWidth()} ref={footerRef}>
          <StyledLink onClick={billingRedirection}>Back</StyledLink>
          <Button
            type="primary"
            minWidth="auto"
            onClick={() =>
              setCurrentStep(AnsweringServiceSubscribeStep.PaymentConfirmation)
            }
            dataCy="listings-continue-button"
          >
            Continue
          </Button>
        </StyledFooter>
      )}
    </>
  );
};

export default ManageListingsLayout;

const BackArrow = styled.button`
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid #1890ff;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const StyledLink = styled(Link)`
  color: black;
  margin-right: 25px;
`;

const StyledFooter = styled.div<{ $left: number }>`
  border: 1px solid #f0f0f0;
  background-color: white;
  display: flex;
  justify-content: right;
  align-items: center;
  position: sticky;
  bottom: 0;
  height: 67px;
  min-width: 924px;
  width: 100%;
  padding-right: 43px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #1a1b21;
  }
`;

const StyledListingsTableContainer = styled(Card)<{ $marginBottom?: number }>`
  grid-area: span 1 / span 2;
  .ant-card-body {
    padding: 0;
  }
  ${({ $marginBottom }) =>
    $marginBottom && `margin-bottom: ${$marginBottom}px`};
`;

const StyledManageListingsContainer = styled.div<{
  $hasCards?: boolean;
}>`
  min-width: 924px;
  display: grid;
  grid-template: ${({ $hasCards }) =>
    $hasCards
      ? `${TOGGLE_CARD_HEIGHT - 5}px auto / 2.5fr 1.5fr`
      : `auto / 1fr`};
  width: 100%;
  gap: 5px;
`;
